import React, { useEffect, useState } from 'react';

import { Select, SelectProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteStaff from 'hooks/staff/useAutoCompleteStaff';
import { Staff } from 'types';

export interface IMovementSourceSelectOption extends Pick<Staff, 'staffId' | 'fullName'> {}

interface MovementSourceSelectProps extends Omit<SelectProps, 'data'> {
  value?: string | null;
  onChange?: (value: string | null) => void;
}

const DEFAULT_OPTION: IMovementSourceSelectOption = { staffId: 'default', fullName: 'Default' };

const MovementSourceSelect = ({
  value,
  onChange,
  ...selectProps
}: MovementSourceSelectProps): JSX.Element => {
  const [autocompleteData, setAutocompleteData] = useState<IMovementSourceSelectOption[]>([]);
  const [sourceSearch, setSourceSearch] = useState<string>('');
  const [debouncedSourceSearchTerm] = useDebouncedValue<string>(sourceSearch, 250);
  const { data, isSuccess } = useAutoCompleteStaff({
    search: debouncedSourceSearchTerm,
    isCoach: true,
  });
  const [selectedItem, setSelectedItem] = useState<IMovementSourceSelectOption | null>(null);

  useEffect(() => {
    if (!value || value === 'default') {
      setSelectedItem(null);
    }
  }, [value]);

  const handleSearchChange = (search: string): void => {
    setSourceSearch(search);
  };

  useEffect(() => {
    if (isSuccess && data?.data) {
      const results = [DEFAULT_OPTION].concat(data.data as Pick<Staff, 'staffId' | 'fullName'>[]);
      const extra =
        selectedItem &&
        selectedItem.staffId !== 'default' &&
        !results.some(r => r.staffId === selectedItem.staffId)
          ? [selectedItem]
          : [];
      setAutocompleteData(results.concat(extra));
    }
  }, [isSuccess, data, selectedItem]);

  const handleChange = (v: string | null): void => {
    if (onChange) {
      onChange(v);
    }
    const newSelected =
      v === null || v === undefined ? null : autocompleteData.find(item => item.staffId === v) ?? null;
    setSelectedItem(newSelected);
  };

  return (
    <Select
      {...selectProps}
      value={value}
      onChange={handleChange}
      data={autocompleteData.map(item => ({
        value: item.staffId,
        label: item.fullName,
      }))}
      clearable
      searchable
      placeholder='Filter by Source'
      nothingFound='No coach found'
      onSearchChange={handleSearchChange}
    />
  );
};

export default MovementSourceSelect;
